import { FormEvent, useState } from 'react';
import { Bug, X } from 'lucide-react';
import { cloud } from '@/lib/climbwriterCloud';

export default function BugReportWidget() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!cloud || !consent || sending || sent) return;
    setSending(true); setMessage('');
    try {
      const { data, error } = await cloud.functions.invoke('submit-bug-report', { body: {
        title: title.trim(), description: description.trim(), stepsToReproduce: steps.trim(),
        contactEmail: contactEmail.trim(), consent: true,
        pageUrl: `${window.location.origin}${window.location.pathname}`, routePath: window.location.pathname,
        userAgent: navigator.userAgent, viewport: `${window.innerWidth}×${window.innerHeight}`,
      }});
      if (error || !data?.success) {
        let detail = data?.error;
        if (error?.context instanceof Response) { try { detail = (await error.context.json()).error; } catch { /* Use fallback below. */ } }
        throw new Error(detail || 'The report could not be sent. Please try again.');
      }
      setSent(true); setMessage('Report sent. Thank you for helping improve ClimbWriter.');
      setTitle(''); setDescription(''); setSteps(''); setContactEmail(''); setConsent(false);
    } catch (error) { setMessage(error instanceof Error ? error.message : 'The report could not be sent. Please try again.'); }
    finally { setSending(false); }
  };

  return <>
    <button className="cw-bug-trigger" type="button" onClick={() => { setOpen(true); if(sent) { setSent(false); setMessage(''); } }} aria-label="Report a bug"><span>Report bug</span><Bug size={18}/></button>
    {open && <div className="cw-bug-overlay" onMouseDown={event => { if (event.target === event.currentTarget) setOpen(false); }}>
      <form className="cw-bug-dialog" onSubmit={event=>void submit(event)} role="dialog" aria-modal="true" aria-labelledby="cw-bug-title">
        <header><div><h2 id="cw-bug-title">Report a bug</h2><p>Tell us what went wrong. Page and browser details are added automatically.</p></div><button type="button" onClick={() => setOpen(false)} aria-label="Close bug report"><X size={19}/></button></header>
        <label>Short title <span>optional</span><input value={title} onChange={event => setTitle(event.target.value)} maxLength={120}/></label>
        <label>What happened?<textarea required value={description} onChange={event => setDescription(event.target.value)} rows={5} maxLength={3000}/></label>
        <label>Steps to reproduce <span>optional</span><textarea value={steps} onChange={event => setSteps(event.target.value)} rows={3} maxLength={2500}/></label>
        <label>Contact email <span>optional, if you want a reply</span><input type="email" value={contactEmail} onChange={event=>setContactEmail(event.target.value)} maxLength={254}/></label>
        <label className="cw-bug-consent"><input type="checkbox" required checked={consent} onChange={event=>setConsent(event.target.checked)}/><span>I agree to send this report and page/browser details to our support system in Linear. No writing drafts are attached. <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>.</span></label>
        {message && <p role="status" aria-live="polite">{message}</p>}
        {!cloud && <p role="status">Reporting is unavailable. Email <a href="mailto:info@climbwriter.com">info@climbwriter.com</a>.</p>}
        <footer><button type="button" onClick={() => setOpen(false)}>{sent?'Close':'Cancel'}</button>{!sent&&<button type="submit" disabled={!cloud || !consent || sending}>{sending?'Sending…':'Send report'}</button>}</footer>
      </form>
    </div>}
  </>;
}
